import React, { useState, useMemo } from 'react';
import { Search, AlertTriangle, TrendingUp, TrendingDown } from 'lucide-react';

/**
 * Компонент для отображения таблицы курсов валют
 * 
 * @param {Object} exchangeRates - Объект с обменными курсами
 * @param {Object} currencies - Объект с информацией о валютах
 * @param {string} baseCurrency - Базовая валюта
 * @param {boolean} loading - Флаг загрузки данных
 */
const RateTable = ({ exchangeRates, currencies, baseCurrency = 'USD', loading }) => {
  // Состояние для поиска
  const [searchQuery, setSearchQuery] = useState('');
  
  // Состояние для сортировки
  const [sortConfig, setSortConfig] = useState({
    key: 'code',
    direction: 'ascending'
  });
  
  // Функция для сортировки списка валют
  const sortedCurrencies = useMemo(() => {
    // Фильтруем все валюты по поисковому запросу
    const filteredCurrencies = Object.values(currencies).filter(currency => {
      const query = searchQuery.toLowerCase();
      return (
        currency.code.toLowerCase().includes(query) ||
        currency.name.toLowerCase().includes(query)
      );
    });
    
    // Сортируем отфильтрованный список
    return [...filteredCurrencies].sort((a, b) => {
      if (sortConfig.key === 'code') {
        return sortConfig.direction === 'ascending' 
          ? a.code.localeCompare(b.code)
          : b.code.localeCompare(a.code);
      } else if (sortConfig.key === 'name') {
        return sortConfig.direction === 'ascending' 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else if (sortConfig.key === 'rate') {
        const rateA = exchangeRates[a.code] ? 1 / exchangeRates[a.code] : 0;
        const rateB = exchangeRates[b.code] ? 1 / exchangeRates[b.code] : 0;
        return sortConfig.direction === 'ascending' 
          ? rateA - rateB
          : rateB - rateA;
      }
      return 0;
    });
  }, [currencies, exchangeRates, searchQuery, sortConfig]);
  
  // Функция для изменения параметров сортировки
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'ascending' 
        ? 'descending' 
        : 'ascending'
    }));
  };
  
  // Функция для расчета курса валюты относительно базовой
  const calculateRate = (currency) => {
    if (!exchangeRates[currency] || !exchangeRates[baseCurrency]) {
      return '-';
    }
    
    // Рассчитываем курс относительно базовой валюты
    const rate = exchangeRates[currency] / exchangeRates[baseCurrency];
    return rate.toFixed(4);
  };
  
  // Функция для расчета изменения курса (здесь просто случайное значение для демонстрации)
  const calculateChange = (currency) => {
    // В реальности здесь должно быть сравнение с предыдущим значением курса
    // Для демонстрации генерируем случайное значение от -3% до +3%
    const seed = currency.charCodeAt(0) + currency.charCodeAt(1);
    const change = (Math.sin(seed) * 3).toFixed(2);
    return {
      value: change,
      isPositive: parseFloat(change) >= 0
    };
  };
  
  // Функция для получения класса ячейки с изменением курса
  const getChangeClass = (isPositive) => {
    return isPositive
      ? 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20'
      : 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20';
  };
  
  // Функция для получения иконки изменения курса
  const getChangeIcon = (isPositive) => {
    return isPositive
      ? <TrendingUp className="h-3.5 w-3.5" />
      : <TrendingDown className="h-3.5 w-3.5" />;
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 mb-8">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
        <TrendingUp className="mr-2 h-6 w-6 text-indigo-500" />
        Курсы основных валют
      </h2>
      
      {/* Поисковая строка */}
      <div className="mb-4 relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="Поиск валюты..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      
      {/* Информация о базовой валюте */}
      <div className="mb-4 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-md text-indigo-700 dark:text-indigo-300 text-sm">
        Все курсы указаны относительно <strong>{baseCurrency}</strong> ({currencies[baseCurrency]?.name || 'базовая валюта'})
      </div>
      
      {/* Таблица курсов */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead>
            <tr>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                onClick={() => handleSort('code')}
              >
                <div className="flex items-center justify-between">
                  <span>Код</span>
                  {sortConfig.key === 'code' && (
                    <span>{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                  )}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center justify-between">
                  <span>Название</span>
                  {sortConfig.key === 'name' && (
                    <span>{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                  )}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                onClick={() => handleSort('rate')}
              >
                <div className="flex items-center justify-end">
                  <span>Курс к {baseCurrency}</span>
                  {sortConfig.key === 'rate' && (
                    <span className="ml-1">{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                  )}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Изменение
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan="4" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                  <div className="flex justify-center items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Загрузка курсов валют...
                  </div>
                </td>
              </tr>
            ) : sortedCurrencies.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                  {searchQuery ? 'Валюты не найдены' : 'Нет доступных валют'}
                </td>
              </tr>
            ) : (
              sortedCurrencies.map(currency => {
                const change = calculateChange(currency.code);
                
                return (
                  <tr key={currency.code} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      <div className="flex items-center">
                        <span className="flex-shrink-0 w-8 text-center">{currency.symbol}</span>
                        <span className="ml-1 font-semibold">{currency.code}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                      {currency.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900 dark:text-white">
                      {calculateRate(currency.code)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${getChangeClass(change.isPositive)}`}>
                        {getChangeIcon(change.isPositive)}
                        <span className="ml-1">{change.isPositive ? '+' : ''}{change.value}%</span>
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      
      <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
        * Изменения курсов указаны за последние 24 часа.
      </div>
    </div>
  );
};

export default RateTable;